import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { bucketUrlPrefix } from "common/config";
import { type UnifiedSuggestItem } from "common/apis";
import { useFilters, useI18N, useBrands } from "common/hooks";
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  TrainIcon,
  AirplaneTakeoffIcon,
  BuildingsIcon,
} from "@phosphor-icons/react";
import {
  CookieIcon,
  CoffeeIcon,
  MartiniIcon,
  ForkKnifeIcon,
  ShoppingCartIcon,
} from "@phosphor-icons/react";
import {
  PlainRingPin,
  VerifiedSociabilityPin,
  PIN_ASPECT,
  getPinCentreTop,
  RING_STROKE,
} from "@/components/Map/AdvancedPinContent";
import { getPinColor } from "@/components/Map/pinColors";
import { useDarkMode } from "@/hooks/useDarkMode";
import { VenuePinType } from "common/types";
import { BusinessTypeGlyph } from "@/components/Map/BusinessTypeGlyph";

// Presentation for one unified auto-suggest item, shared by every surface that
// lists them (the search bar, the change-location modal's search mode).

// Circular icon badge: purple for locations/stations/categories, grey for search.
export const IconBadge = ({
  tone,
  children,
}: {
  tone: "purple" | "grey";
  children: ReactNode;
}) => (
  <div
    className={cn(
      "flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0",
      tone === "purple" ? "bg-bg-brand-tertiary-active" : "bg-pin-grey",
    )}
  >
    {children}
  </div>
);

// Same pin used on the map (orange+verified for mapped venues, grey outline
// for unmapped), with the business-type glyph centred inside. Sized to the
// same 32px box as the circular badges so every row's icon column lines up.
// The svg frame is wider than the pin itself, so it's scaled up to 36 to land
// the head/tail at the badges' 32px optical size.
const VENUE_PIN_SIZE = 36;
const VenuePinIcon = ({
  pinType,
  businessType,
}: {
  pinType: "orange" | "grey";
  businessType: string | null;
}) => {
  const isDark = useDarkMode();
  const orange = getPinColor(VenuePinType.Orange, isDark);
  const color = pinType === "grey" ? RING_STROKE : orange;
  const glyphSize = VENUE_PIN_SIZE * 0.4;
  const centreTop = getPinCentreTop(VENUE_PIN_SIZE);

  return (
    <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
      <div
        className="relative"
        style={{ width: VENUE_PIN_SIZE, height: VENUE_PIN_SIZE * PIN_ASPECT }}
      >
        {pinType === "grey" ? (
          <PlainRingPin size={VENUE_PIN_SIZE} color={color} />
        ) : (
          <VerifiedSociabilityPin size={VENUE_PIN_SIZE} accent={orange} />
        )}
        <span
          className="absolute pointer-events-none"
          style={{ left: "50%", top: centreTop, transform: "translate(-50%, -50%)" }}
        >
          <BusinessTypeGlyph businessType={businessType} size={glyphSize} />
        </span>
      </div>
    </div>
  );
};

// Component to display business type icons
const BusinessTypeIcon = ({ businessType }: { businessType: string }) => {
  const { businessTypesFrontend } = useFilters();
  const [iconPath, setIconPath] = useState<string | null>(null);

  // First check for specific hardcoded business type icons
  const hasSpecificIcon = [
    "bakery",
    "cafe",
    "bar",
    "restaurant",
    "supermarket",
    "food",
    "store",
  ].includes(businessType);

  // Find the matching business type and get its icon from businessTypesFrontend
  useEffect(() => {
    // Skip fetching if we have a specific icon
    if (hasSpecificIcon) return;

    if (businessTypesFrontend && businessType) {
      const matchingFrontendType = businessTypesFrontend.find(
        (type) => type.name.toLowerCase() === businessType.toLowerCase(),
      );
      if (matchingFrontendType && matchingFrontendType.image) {
        setIconPath(bucketUrlPrefix + matchingFrontendType.image);
        return;
      }
    }
  }, [businessTypesFrontend, businessType, hasSpecificIcon]);

  // Render specific icon if available
  if (hasSpecificIcon) {
    const icons: Record<string, ReactNode> = {
      bakery: <CookieIcon size={16} weight="fill" className="text-fg-brand-subtle" />,
      cafe: <CoffeeIcon size={16} weight="fill" className="text-fg-brand-subtle" />,
      bar: <MartiniIcon size={16} weight="fill" className="text-fg-brand-subtle" />,
      restaurant: (
        <ForkKnifeIcon size={16} weight="fill" className="text-fg-brand-subtle" />
      ),
      food: (
        <ShoppingCartIcon size={16} weight="fill" className="text-fg-brand-subtle" />
      ),
      supermarket: (
        <ShoppingCartIcon size={16} weight="fill" className="text-fg-brand-subtle" />
      ),
      store: (
        <ShoppingCartIcon size={16} weight="fill" className="text-fg-brand-subtle" />
      ),
    };
    if (icons[businessType]) {
      return <IconBadge tone="purple">{icons[businessType]}</IconBadge>;
    }
  }

  // Render dynamic icon from businessTypesFrontend if found
  if (iconPath) {
    return (
      <IconBadge tone="purple">
        <img
          src={iconPath}
          alt={businessType}
          className="w-4 h-4 object-contain filter brightness-0"
        />
      </IconBadge>
    );
  }

  // Fallback icon when the business type isn't one we support yet
  return (
    <IconBadge tone="purple">
      <BuildingsIcon size={16} weight="fill" className="text-fg-brand-subtle" />
    </IconBadge>
  );
};

// Component to display brand logo or first character
interface BrandLogoProps {
  brandName: string;
  latitude: number;
  longitude: number;
}

const BrandLogo = ({ brandName, latitude, longitude }: BrandLogoProps) => {
  const { data: brandsData } = useBrands({
    latitude,
    longitude,
    enabled:
      !!brandName &&
      !!latitude &&
      !!longitude &&
      !isNaN(latitude) &&
      !isNaN(longitude),
  });

  const matchingBrand = brandsData?.facets?.brand?.find(
    (b) => b.value.toLowerCase() === brandName.toLowerCase(),
  );

  const imageUrl = matchingBrand?.logo
    ? `${bucketUrlPrefix}${matchingBrand.logo}`
    : null;

  // With a logo it's the image in a 32px circle; without one, the same grey
  // badge every other iconless row uses, carrying the brand's initial.
  if (imageUrl) {
    return (
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-container-neutral-minimal overflow-hidden flex-shrink-0">
        <img
          src={imageUrl}
          alt={brandName}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <IconBadge tone="grey">
      <span className="label-m-semibold text-fg-neutral-strong">
        {brandName.charAt(0).toUpperCase()}
      </span>
    </IconBadge>
  );
};

/** iconKey → icon; in-DB venues use the orange/grey pin from data.pinType. */
export const SuggestItemIcon = ({
  item,
  latitude,
  longitude,
}: {
  item: UnifiedSuggestItem;
  latitude?: number;
  longitude?: number;
}) => {
  const { iconKey, data } = item;

  if (data.pinType === "orange" || data.pinType === "grey") {
    return <VenuePinIcon pinType={data.pinType} businessType={data.businessType} />;
  }

  switch (iconKey) {
    case "venue":
      // Not (yet) in our DB — same grey pin as an unmapped venue, no
      // separate "generic place" icon.
      return <VenuePinIcon pinType="grey" businessType={data.businessType} />;
    case "brand":
      return (
        <BrandLogo
          brandName={data.brand || ""}
          latitude={latitude || 0}
          longitude={longitude || 0}
        />
      );
    case "area":
      return (
        <IconBadge tone="purple">
          <MapPinIcon size={16} weight="fill" className="text-fg-brand-subtle" />
        </IconBadge>
      );
    case "station":
      return (
        <IconBadge tone="purple">
          <TrainIcon size={16} weight="fill" className="text-fg-brand-subtle" />
        </IconBadge>
      );
    case "airport":
      return (
        <IconBadge tone="purple">
          <AirplaneTakeoffIcon
            size={16}
            weight="fill"
            className="text-fg-brand-subtle"
          />
        </IconBadge>
      );
    case "search":
      return (
        <IconBadge tone="grey">
          <MagnifyingGlassIcon size={16} weight="bold" className="text-fg-neutral-strong" />
        </IconBadge>
      );
    case "location":
      return (
        <IconBadge tone="purple">
          <MapPinIcon size={16} weight="fill" className="text-fg-brand-subtle" />
        </IconBadge>
      );
    default:
      // Any other iconKey is a business-type slug ("cafe", "bar", ...).
      return <BusinessTypeIcon businessType={iconKey} />;
  }
};

/** Render displayText with the backend-computed highlight ranges in bold. */
export const renderHighlighted = (text: string, ranges: [number, number][]) => {
  if (!text) return <>{text}</>;
  if (!ranges || ranges.length === 0) return <>{text}</>;
  const sorted = [...ranges]
    .filter((r) => Array.isArray(r) && r.length === 2)
    .sort((a, b) => a[0] - b[0]);
  if (sorted.length === 0) return <>{text}</>;
  const parts: ReactNode[] = [];
  let cursor = 0;
  sorted.forEach(([start, end], i) => {
    const s = Math.max(start, cursor);
    if (s > end) return;
    if (s > cursor) {
      parts.push(<span key={`n${i}`}>{text.slice(cursor, s)}</span>);
    }
    parts.push(<b key={`b${i}`}>{text.slice(s, end)}</b>);
    cursor = Math.max(cursor, end);
  });
  if (cursor < text.length) {
    parts.push(<span key="rest">{text.slice(cursor)}</span>);
  }
  return <>{parts}</>;
};

/** Backend subtitle plus a CTA suffix keyed off action type. A hook because the CTA is translated. */
export function useSubtitleLine() {
  const i18n = useI18N();

  return (item: UnifiedSuggestItem): string => {
    let cta = "";
    switch (item.action.type) {
      case "PAN_AND_NEARBY":
        cta =
          item.iconKey === "airport"
            ? i18n.get("searchNearAirport")
            : item.iconKey === "station"
              ? i18n.get("searchNearStation")
              : i18n.get("searchThisArea");
        break;
      case "CATEGORY_IN_AREA":
        cta = i18n.get("searchInThisArea");
        break;
      case "BRAND":
        cta = i18n.get("seeLocations");
        break;
      default:
        cta = "";
    }
    return [item.subtitle, cta].filter(Boolean).join(" • ");
  };
}

/** Icon + bolded title + subtitle. The caller owns the surrounding button. */
export const SuggestItemRow = ({
  item,
  latitude,
  longitude,
}: {
  item: UnifiedSuggestItem;
  latitude?: number;
  longitude?: number;
}) => {
  const subtitleLine = useSubtitleLine();
  const subtitle = subtitleLine(item);

  return (
    <div className="flex items-center gap-3 w-full">
      <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
        <SuggestItemIcon item={item} latitude={latitude} longitude={longitude} />
      </div>
      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
        <span className="label-m-semibold text-fg-neutral-strong truncate max-w-full">
          {renderHighlighted(item.displayText, item.highlightRanges)}
        </span>
        {subtitle && (
          <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
};
